import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import Swal from "sweetalert2";
import api from "../../api/api";

const Expenditure = () => {
  const [expenditures, setExpenditures] = useState([]);
  const [newExpenditure, setNewExpenditure] = useState({
    title: "", amount: "", category: "building_maintenance", description: ""
  });
  const [editId, setEditId] = useState(null);
  const [searchEx, setSearchEx] = useState("");
  const [pageEx, setPageEx] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const rowsPerPage = 6;

  const token = localStorage.getItem("access");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchExpenditures = async () => {
    try {
      const res = await api.get(`/expenditures/`, { headers });
      setExpenditures(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchExpenditures();
  }, []);

  const handleCreateOrUpdateExpenditure = async () => {
    setSubmitLoading(true);
    try {
      if (editId) {
        await api.put(`/expenditures/${editId}/`, newExpenditure, { headers });
        Swal.fire("Updated", "Expenditure updated successfully", "success");
      } else {
        await api.post(`/expenditures/`, newExpenditure, { headers });
        Swal.fire("Success", "Expenditure recorded successfully", "success");
      }
      setNewExpenditure({ title: "", amount: "", category: "building_maintenance", description: "" });
      setEditId(null);
      fetchExpenditures();
    } catch {
      Swal.fire("Error", "Failed to save expenditure", "error");
    } finally {
      setTimeout(() => setSubmitLoading(false), 3000);
    }
  };

  const handleEdit = ex => {
    setNewExpenditure(ex);
    setEditId(ex.id);
  };

  const handleDelete = async id => {
    const res = await Swal.fire({
      title: "Confirm Deletion",
      text: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete!"
    });
    if (res.isConfirmed) {
      try {
        await api.delete(`/expenditures/${id}/`, { headers });
        Swal.fire("Deleted!", "Expenditure has been deleted.", "success");
        fetchExpenditures();
      } catch {
        Swal.fire("Error", "Failed to delete", "error");
      }
    }
  };

  const filteredEx = expenditures.filter(e =>
    e.title.toLowerCase().includes(searchEx.toLowerCase())
  );
  const displayEx = filteredEx.slice(pageEx * rowsPerPage, pageEx * rowsPerPage + rowsPerPage);

  return (
    <Box component={Paper} p={2} mb={4}>
      <Typography variant="h6">{editId ? "Edit" : "Create"} Expenditure</Typography>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Title" fullWidth
            value={newExpenditure.title}
            onChange={e => setNewExpenditure({ ...newExpenditure, title: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Amount" type="number" fullWidth
            value={newExpenditure.amount}
            onChange={e => setNewExpenditure({ ...newExpenditure, amount: e.target.value })}
          />
        </Grid>

        {/* UPDATED CATEGORY LIST */}
        <Grid item xs={12} md={3}>
          <TextField
            select label="Category" fullWidth
            value={newExpenditure.category}
            onChange={e => setNewExpenditure({ ...newExpenditure, category: e.target.value })}
          >
            {[
              "building_maintenance",
              "utilities",
              "supplies",
              "events",
              "transportation",
              "miscellaneous",
              "benevolence_gratuity",
              "loan_payment",
              "furniture_purchase",
              "administrative_cost",
              "mmc_senior_pastor_support",
              "instructional_supplies",
              "communication",
              "office_supplies",
              "graduation_program",
              "travel_cost",
              "ground_maintenance",
              "Purchase",
              "equipment_purchase",
              "janatorial_supplies",
              "utilities_electricity",
            ].map(cat => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Description" fullWidth
            value={newExpenditure.description}
            onChange={e => setNewExpenditure({ ...newExpenditure, description: e.target.value })}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained" color="primary"
        onClick={handleCreateOrUpdateExpenditure}
        sx={{ mt: 2 }}
        disabled={submitLoading}
      >
        {submitLoading ? <CircularProgress size={24} /> : (editId ? "Update" : "Add Expenditure")}
      </Button>

      <Typography variant="h6" mt={4}>All Expenditures</Typography>
      <TextField
        label="Search by title" fullWidth
        value={searchEx}
        onChange={e => setSearchEx(e.target.value)}
        sx={{ my: 2 }}
      />

      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayEx.map(ex => (
              <TableRow
                key={ex.id}
                hover
                sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
              >
                <TableCell>{ex.title}</TableCell>
                <TableCell>${ex.amount}</TableCell>
                <TableCell>{ex.category}</TableCell>
                <TableCell>{ex.description}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(ex)}><Edit /></IconButton>
                  <IconButton onClick={() => handleDelete(ex.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredEx.length}
        page={pageEx}
        onPageChange={(e, newPage) => setPageEx(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
      />
    </Box>
  );
};

export default Expenditure;